import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  MAX_PRIMARY_ENTITY_SELECTIONS,
  MAX_SECONDARY_FILTER_SELECTIONS,
} from '@revelio/iso-utility';
import { Skeleton, type TNode, type TreeApi } from '@revelio/ui';

import {
  AllCompaniesPopoutTree,
  type ShortlistRenderer,
  type ShortlistToggle,
} from '../../../../../shared/components/tree/PopoutTree/Async/AllCompaniesPopoutTree';
import { SelectedValuesDisplayComponent } from '../../../../../shared/components/tree/PopoutTree/Async/SelectedValueDisplay';
import { PopoutTree } from '../../../../../shared/components/tree/PopoutTree/PopoutTree';
import treeStyles from '../../../../../shared/components/tree/PopoutTree/PopoutTree.module.css';
import {
  type ChipSource,
  RecentEntitiesPanel,
  type RecentEntity,
} from '../../../../../shared/components/tree/PopoutTree/RecentEntitiesPanel';
import { SHORTCUT_SOURCE_BY_CHIP } from '../../../../../shared/components/tree/shortcut-source';
import treeSkeletonStyles from '../../../../../shared/components/tree/tree-skeleton.module.css';
import { useTreeSelections } from '../../../../../shared/components/tree/use-tree-selections';
import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useActivityLevelLabels } from '../../../../../shared/selection-lists/hooks/useActivityLevelLabels';
import { useCustomSkillLevelLabels } from '../../../../../shared/selection-lists/hooks/useCustomSkillLevelLabels';
import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../components/PageConfigProvider/types';
import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import { ACTIVITY_FILTER_POPOUT_WIDTH } from '../../constants/filter-constants';
import { DEFAULT_PRIMARY_COMPANIES } from '../../contexts/defaultFilters';
import {
  type TreeData,
  useFilterContext,
  useSingleFilterData,
} from '../../contexts/FilterProvider';
import type { CustomActivityTaxonomySelection } from '../../definitions/activityTaxonomy';
import { useCompetitorSuggestions } from '../../hooks/useCompetitorSuggestions';
import { useRecentEntities } from '../../hooks/useRecentEntities';
import { useTrackEntityShortcutAdded } from '../../hooks/useTrackEntityShortcutAdded';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import {
  areTrialAllowedSelections,
  TRIAL_COMPANY_SUBMIT_TOOLTIP,
} from '../../trial/trialCompanyAllowList';
import {
  FilterId,
  FilterType,
  type TreeFilter,
  type TreeSelection,
} from '../../types/filters';
import { getNumericIdFromNestedId } from '../../utils/get-numeric-id-from-nested-id';
import { getPrimarySelectionMatchKey } from '../../utils/primaryFilterUtils';
import { isRicsReferenceFilter } from '../../utils/ricsTaxonomyTree';
import {
  deriveSelectionSource,
  forgetPicks,
  recordPicks,
  type SelectionSourceMap,
  type TreeSelectionAnalytics,
} from '../../utils/selection-source';
import { ToggleSelect } from '../ToggleSelect';
import layout from './filterFormLayout.module.css';
import { GeographyTaxonomyBranchLabel } from './GeographyTaxonomySelect';
import { NotToggle } from './NotToggle';

const findTreeDataById = (
  items: TreeData[],
  idToFind: string
): TreeData | undefined => {
  for (const item of items) {
    if (item.id === idToFind) {
      return item;
    }
    if (item.children) {
      const found = findTreeDataById(item.children, idToFind);
      if (found !== undefined) {
        return found;
      }
    }
  }
  return undefined;
};

interface TreeFilterFormProps {
  filter: TreeFilter;
  closeMenu: () => void;
  disallowMultiLevelSelect: boolean;
  isOpen?: boolean;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  isSelfSignupUser?: boolean;
  onSave: (filter: TreeFilter, analytics?: TreeSelectionAnalytics) => void;
  isNewInstance?: boolean;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
  disabledLevels?: number[];
  maxSelections?: number;
  showSelectedValues?: boolean;
  currentTreeSelections?: TreeSelection[];
  replaceSingleSelections?: boolean;
}

export const TreeFilterForm: React.FC<TreeFilterFormProps> = ({
  filter,
  closeMenu,
  isOpen,
  disallowMultiLevelSelect,
  searchInputRef,
  isSelfSignupUser,
  onSave,
  isNewInstance,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
  disabledLevels: disabledLevelsProp,
  maxSelections,
  showSelectedValues = false,
  currentTreeSelections,
  replaceSingleSelections = false,
}) => {
  const { revelioPageId } = useTabContentConfig();
  const location = useLocation();
  const isRicsTaxonomyTreeEnabled = isRicsReferenceFilter(filter.id);
  const resolvedMaxSelections = (() => {
    if (maxSelections !== undefined) {
      return maxSelections;
    }
    if (filter.isPrimary) {
      return MAX_PRIMARY_ENTITY_SELECTIONS;
    }
    return MAX_SECONDARY_FILTER_SELECTIONS;
  })();

  // A secondary Company filter selects companies only — industries are picked
  // through the dedicated Industry filter. The primary filter keeps the full
  // industry tree, and callers that dictate their own levels (report
  // parameters) keep the tree they configured.
  const isCompanyOnly =
    filter.id === FilterId.COMPANY &&
    !filter.isPrimary &&
    disabledLevelsProp === undefined;

  // Every primary unit of analysis. `usePrimaryFilterState` records recents for
  // all of them, so leaving one out here banks them and never offers them back.
  const recentsFilterId =
    filter.isPrimary &&
    (filter.id === FilterId.COMPANY ||
      filter.id === FilterId.INDUSTRY ||
      filter.id === FilterId.GEOGRAPHY ||
      filter.id === FilterId.ROLE ||
      filter.id === FilterId.CUSTOM_SKILL ||
      filter.id === FilterId.ACTIVITY)
      ? filter.id
      : undefined;
  const { getRecents, removeRecents } = useRecentEntities(recentsFilterId);
  const [recentEntities, setRecentEntities] = useState<TreeSelection[]>([]);

  useEffect(() => {
    if (!recentsFilterId) return;
    if (isOpen ?? true) {
      setRecentEntities(getRecents());
    }
  }, [isOpen, recentsFilterId, getRecents]);

  const handleRemoveRecentEntity = useCallback(
    (entity: RecentEntity) => {
      const stored = recentEntities.find((item) => item.id === entity.id);
      if (!stored) return;
      removeRecents([{ id: stored.id, levelId: stored.levelId }]);
      setRecentEntities((current) =>
        current.filter((item) => item.id !== entity.id)
      );
    },
    [recentEntities, removeRecents]
  );

  const selectionRecordsForSelectedItems =
    (filter.isPrimary ? filter.primarySelections : filter.selections) ?? [];

  const committedSelectionKeys = new Set(
    selectionRecordsForSelectedItems.map(getPrimarySelectionMatchKey)
  );
  const activeLevelIds = filter.selectionListIds
    ? new Set<string>(filter.selectionListIds)
    : undefined;
  const availableRecentEntities = recentsFilterId
    ? recentEntities.filter(
        (entity) =>
          (!activeLevelIds || activeLevelIds.has(entity.levelId)) &&
          !committedSelectionKeys.has(getPrimarySelectionMatchKey(entity))
      )
    : [];

  // Competitors are a switchable source now, not just the fallback for an
  // empty recents list — the panel cannot offer the option it has no entities
  // for. Deliberately NOT gated on `isOpen`: the popover isn't lazy, so this
  // form is mounted from page load, and resolving the first selection's
  // competitors then means the panel is populated by the time the picker is
  // opened rather than opening onto a loading placeholder. It is one query for
  // one company, and the id it keys on only changes when that selection does.
  const {
    suggestions: competitorSuggestions,
    isLoading: isLoadingCompetitors,
  } = useCompetitorSuggestions({
    enabled: !isSelfSignupUser && recentsFilterId === FilterId.COMPANY,
    firstSelection: filter.primarySelections?.[0],
  });
  const availableCompetitorSuggestions = competitorSuggestions.filter(
    (entity) => !committedSelectionKeys.has(getPrimarySelectionMatchKey(entity))
  );

  const data = useSingleFilterData(filter.id);
  const treeData = (() => {
    return data as TreeData[] | null;
  })();

  const showNodeCount =
    filter.id !== FilterId.ROLE || Boolean(filter.isPrimary);

  // Fetch labels for custom skill taxonomy directly from API (no per-node duplication)
  const { labelsByDepth } = useCustomSkillLevelLabels(0);
  // Labels must come from the SAME taxonomy the tree renders (the resolved
  // adaptive/preference id from filter state), or the column headers would
  // describe taxonomy 0 while the nodes come from another taxonomy. Matching
  // the provider's tree request also lets urql serve both from one fetch.
  const {
    activeFilterMap,
    activityTaxonomyEnabled: isActivityTaxonomyEnabled,
  } = useFilterContext();
  const activityTaxonomyFilter = activeFilterMap.get(
    FilterId.ACTIVITY_TAXONOMY
  );
  const activityTaxonomyValue =
    activityTaxonomyFilter?.type === FilterType.VALUE
      ? (activityTaxonomyFilter.value as CustomActivityTaxonomySelection)
      : undefined;
  const { labelsByDepth: activityLabelsByDepth } = useActivityLevelLabels({
    enabled: isActivityTaxonomyEnabled && filter.id === FilterId.ACTIVITY,
    taxonomyId: activityTaxonomyValue?.taxonomyId ?? 0,
    taxonomyVersion: activityTaxonomyValue?.version,
  });

  // Taxonomy-backed trees ALWAYS get the dynamic branches function: its
  // `Level N` fallback keeps the level-header row stable while (or if) the
  // level-label query is still loading or errored.
  const dynamicBranches = useMemo(() => {
    const levelLabels = (() => {
      if (filter.id === FilterId.CUSTOM_SKILL) return labelsByDepth;
      if (filter.id === FilterId.ACTIVITY) return activityLabelsByDepth;
      return undefined;
    })();
    if (
      filter.id !== FilterId.CUSTOM_SKILL &&
      filter.id !== FilterId.ACTIVITY
    ) {
      return filter.branches;
    }
    return (parentNode: TNode) => {
      const childrenDepth = parentNode.level + 1;
      const label = levelLabels?.[childrenDepth];
      return { label: label || `Level ${childrenDepth}` };
    };
  }, [filter.branches, labelsByDepth, activityLabelsByDepth, filter.id]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const isNotEnabled = isNotEnabledProp ?? filter.isNot ?? false;

  useEffect(() => {
    if (treeData) {
      setHasFetched(true);
    }
  }, [treeData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const sourceSelections =
      (filter.isPrimary ? filter.primarySelections : filter.selections) ?? [];
    const baseIds = sourceSelections.map((s) => s.id);
    const idsToShow = (() => {
      if (!treeData?.length || baseIds.length === 0) {
        return baseIds;
      }
      const flattenIds = (nodes: TreeData[]): string[] =>
        nodes.flatMap((n) => [
          n.id,
          ...(n.children ? flattenIds(n.children) : []),
        ]);
      const allNodeIds = new Set(flattenIds(treeData));
      const selectionListIds = filter.selectionListIds ?? [];
      // Same source as baseIds — a levelId-less lookup leaves depth at -1 and
      // disables the level gating below.
      const idToLevelId = new Map(
        sourceSelections
          .filter(
            (s): s is TreeSelection => s?.id != null && s?.levelId != null
          )
          .map((s) => [String(s.id), s.levelId])
      );

      const resolved = baseIds.flatMap((id) => {
        const levelId = idToLevelId.get(id);
        const depth =
          levelId !== undefined ? selectionListIds.indexOf(levelId) : -1;
        // A node id only counts as this selection when it sits at the
        // selection's declared level: tree node ids are ancestor-path
        // compounds, so a bare (path-less) id can collide with an unrelated
        // top-level node that shares the numeric id.
        const matchesDeclaredDepth = (nodeId: string) =>
          depth < 0 || nodeId.split('_').length === depth + 1;
        if (allNodeIds.has(id) && matchesDeclaredDepth(id)) {
          return [id];
        }

        const suffixMatch = Array.from(allNodeIds).filter(
          (nodeId) =>
            (nodeId === id || nodeId.endsWith('_' + id)) &&
            matchesDeclaredDepth(nodeId)
        );
        if (suffixMatch.length > 0) return suffixMatch;

        const numericId = getNumericIdFromNestedId(id);
        if (numericId !== null && levelId !== undefined && depth >= 0) {
          const match = Array.from(allNodeIds).find((nodeId) => {
            const nodeDepth = nodeId.split('_').length - 1;
            const nodeNumeric = getNumericIdFromNestedId(nodeId);
            return nodeDepth === depth && nodeNumeric === numericId;
          });
          if (match !== undefined) return [match];
        }

        // A depth-mismatched id that exists in the tree must stay unresolved
        // rather than tick the colliding node.
        return allNodeIds.has(id) ? [] : [id];
      });
      return [...new Set(resolved)];
    })();
    if (!isEqual(selectedIds, idsToShow)) {
      setSelectedIds(idsToShow);
    }
  }, [
    filter.id,
    filter.selections,
    filter.primarySelections,
    filter.selectionListIds,
    filter.isPrimary,
    treeData,
    selectedIds,
  ]);

  /**
   * A tree node id is a compound of its ancestor path (`8_257_919809`), so its
   * depth is its segment count and the filter's `selectionListIds` names the
   * level at that depth.
   */
  const levelIdForSelectionId = useCallback(
    (id: string) =>
      filter?.selectionListIds?.[Math.max(0, id.split('_').length - 1)],
    [filter?.selectionListIds]
  );

  const handleApply = useCallback(
    (
      treeSelectedIds: string[],
      selectedNodes?: TNode[],
      pickSources?: SelectionSourceMap
    ) => {
      if (!filter || !treeSelectedIds.length) return;

      // A company checked in the section above the tree may not be in the
      // loaded tree data at all, so its name has to come from here or the chip
      // lands labelled with a raw id.
      const knownSelectionsById = new Map(
        [
          ...(filter.selections || []),
          ...recentEntities,
          ...competitorSuggestions,
          ...(isSelfSignupUser ? DEFAULT_PRIMARY_COMPANIES : []),
        ]
          .filter((s) => s?.id && s?.name)
          .map((s) => [s.id, s])
      );

      const newSelections: TreeSelection[] = treeSelectedIds
        .map((id): TreeSelection | null => {
          if (!id || typeof id !== 'string') return null;

          const selectedNode = selectedNodes?.find((n) => n.id === id);
          const treeItem =
            selectedNode?.data || findTreeDataById(treeData || [], id);

          const known = knownSelectionsById.get(id);
          const levelId = known?.levelId ?? levelIdForSelectionId(id);
          if (!levelId) return null;

          const name = treeItem?.name || known?.name || id;

          return {
            id,
            name,
            levelId,
            isCurrent,
          };
        })
        .filter((s): s is TreeSelection => s !== null);

      const updatedFilter: TreeFilter = {
        ...filter,
        selections: newSelections,
        isCurrent,
        isNot: isNotEnabled,
      };

      onSave(
        updatedFilter,
        deriveSelectionSource(
          selectedIds,
          treeSelectedIds,
          pickSources ?? new Map()
        )
      );
      closeMenu();
    },
    [
      filter,
      closeMenu,
      treeData,
      isCurrent,
      isNotEnabled,
      onSave,
      recentEntities,
      competitorSuggestions,
      isSelfSignupUser,
      levelIdForSelectionId,
      selectedIds,
    ]
  );

  // showToggle: whether to show the Current/Previous toggle UI
  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  // Use tree selections hook for manual control (always call hooks)
  const {
    selections,
    setSelections,
    onChange,
    onClear,
    validateMinSelections,
  } = useTreeSelections({
    initialSelections: selectedIds,
    disallowMultiLevelSelect,
    maxSelections: resolvedMaxSelections,
    minSelections: 0,
    filterLabel: filter.name,
  });

  const popoutTreeRef = useRef<TreeApi>();
  // Where each pending pick came from, for `filters applied`. Starts over each
  // time the picker opens so one form session never inherits another's.
  const pickSourcesRef = useRef<SelectionSourceMap>(new Map());
  const trackFilterReset = useTrackFilterReset();
  const trackShortcutAdded = useTrackEntityShortcutAdded();

  useEffect(() => {
    setSelections(selectedIds);
  }, [selectedIds, setSelections]);

  useEffect(() => {
    if (!isOpen) return;
    pickSourcesRef.current.clear();
    popoutTreeRef.current?.onSearch('');
  }, [isOpen]);

  const handleSubmit = () => {
    if (!validateMinSelections()) {
      return;
    }
    handleApply(selections, undefined, pickSourcesRef.current);
    pickSourcesRef.current.clear();
  };

  // The search text at the moment of the pick is what separates a searched
  // entity from a browsed one; the tree's imperative handle carries it live.
  const handleTreeChange = (value: string[], nodes?: TNode[]) => {
    recordPicks(
      pickSourcesRef.current,
      value,
      popoutTreeRef.current?.search ? 'search' : 'browse'
    );
    onChange(value, nodes);
  };

  const handleClear = () => {
    trackFilterReset(filter, { selectionCountBefore: selections.length });
    pickSourcesRef.current.clear();
    onClear();
  };

  const shouldSortSuggestedSearch =
    filter.id === FilterId.ROLE ||
    filter.id === FilterId.GEOGRAPHY ||
    isRicsTaxonomyTreeEnabled;

  /**
   * A self-signup trial user may only analyse the companies their plan
   * includes, so the picker offers those in place of recents and accepts a
   * submit only when every company checked is one of them. Industry rollups
   * are unrestricted, matching `restrictCompaniesForTrial`.
   */
  const isTrialSelectionSubmittable = useCallback(
    (ids: string[]) => areTrialAllowedSelections(ids, levelIdForSelectionId),
    [levelIdForSelectionId]
  );

  /**
   * A chip carries the entity's own id — for a competitor that is the bare
   * entity id, where the pending selection holds the tree's ancestor-path id
   * for the same company. Matching on levelId + leaf id, as the committed
   * selections already do, is what keeps the chip in step with the tree
   * instead of adding a second entry for a company that is already selected.
   */
  const matchesShortlistEntity = useCallback(
    (pendingId: string, entity: RecentEntity) => {
      if (pendingId === entity.id) return true;
      const levelId = levelIdForSelectionId(pendingId);
      if (!levelId) return false;
      return (
        getPrimarySelectionMatchKey({ id: pendingId, levelId }) ===
        getPrimarySelectionMatchKey(entity)
      );
    },
    [levelIdForSelectionId]
  );

  const isShortlistEntitySelected =
    (pendingIds: string[]) => (entity: RecentEntity) =>
      pendingIds.some((id) => matchesShortlistEntity(id, entity));

  // Recents lead the order — the panel falls back to the first source when it
  // has nothing to show, and "No recents" is the right empty state. Competitors
  // still open first wherever they exist, via `defaultSourceKey`.
  const recentsSources: ChipSource[] = [
    {
      key: 'recent',
      label: 'Recent',
      entities: availableRecentEntities,
      removable: true,
      keepWhenEmpty: true,
    },
    {
      key: 'competitors',
      label: 'Competitors',
      entities: availableCompetitorSuggestions,
      isLoading: isLoadingCompetitors,
    },
  ];

  // Chip adds are counted here, where the filter is known, for both the inline
  // trees and the company picker (whose shortlist this form renders).
  const withShortcutTracking =
    (onToggle: ShortlistToggle): ShortlistToggle =>
    (entity, isSelected, chipSource) => {
      const applied = onToggle(entity, isSelected, chipSource);
      // An add the selection cap refused is not an add.
      if (isSelected && applied) {
        trackShortcutAdded(filter, {
          source: SHORTCUT_SOURCE_BY_CHIP[chipSource],
          levelId: entity.levelId,
        });
      }
      return applied;
    };

  const renderRecentsPanel: ShortlistRenderer = ({
    selectedIds: pendingIds,
    onToggle,
  }) => (
    <RecentEntitiesPanel
      sources={recentsSources}
      defaultSourceKey="competitors"
      isSelected={isShortlistEntitySelected(pendingIds)}
      onToggle={withShortcutTracking(onToggle)}
      onRemove={handleRemoveRecentEntity}
    />
  );

  /**
   * A trial user picks from the companies their plan includes instead of their
   * recents — the only source they get, so the heading stays a plain label.
   */
  const renderTrialShortlist: ShortlistRenderer = ({
    selectedIds: pendingIds,
    onToggle,
  }) => (
    <RecentEntitiesPanel
      sources={[
        {
          key: 'trial',
          label: 'Included in your trial',
          entities: DEFAULT_PRIMARY_COMPANIES,
        },
      ]}
      isSelected={isShortlistEntitySelected(pendingIds)}
      onToggle={withShortcutTracking(onToggle)}
      testId="trial-companies-section"
    />
  );

  let renderShortlist: ShortlistRenderer | undefined;
  if (isSelfSignupUser) {
    renderShortlist = renderTrialShortlist;
  } else if (recentsFilterId) {
    renderShortlist = renderRecentsPanel;
  }

  if (
    (filter.id === FilterId.COMPANY ||
      filter.id === FilterId.COMPETITORS ||
      filter.id === FilterId.FOUNDER_COMPANY) &&
    treeData
  ) {
    return (
      <AllCompaniesPopoutTree
        searchInputRef={searchInputRef}
        treeData={treeData}
        activeIds={selectedIds}
        activeNodeData={currentTreeSelections?.map((s) => ({
          id: s.id,
          name: s.name,
          levelId: s.levelId,
          isCurrent: s.isCurrent,
        }))}
        filterLabel={filter.name}
        branches={dynamicBranches}
        sortSuggestedSearch={shouldSortSuggestedSearch}
        onSubmit={handleApply}
        onClearSelections={(pendingSelectionCount) =>
          trackFilterReset(filter, {
            selectionCountBefore: pendingSelectionCount,
          })
        }
        onTooltipCompanyAdded={(source) =>
          trackShortcutAdded(filter, { source })
        }
        onClose={closeMenu}
        isOpen={isOpen}
        disabledLevels={
          disabledLevelsProp ??
          (revelioPageId === RevelioPageId.COMPANY_OVERVIEW ||
          revelioPageId === RevelioPageId.COMPANY_SCREENER ||
          filter.id === FilterId.COMPETITORS ||
          filter.id === FilterId.FOUNDER_COMPANY
            ? [0, 1]
            : undefined)
        }
        maxSelections={resolvedMaxSelections}
        disallowMultiLevelSelect={disallowMultiLevelSelect}
        companyOnly={isCompanyOnly}
        showSelectedValues={showSelectedValues}
        replaceSingleSelections={replaceSingleSelections}
        renderShortlist={renderShortlist}
        matchesShortlistEntity={matchesShortlistEntity}
        isSelectionSubmittable={
          isSelfSignupUser ? isTrialSelectionSubmittable : undefined
        }
        submitDisabledTooltip={
          isSelfSignupUser ? TRIAL_COMPANY_SUBMIT_TOOLTIP : undefined
        }
        middleContent={
          showNotToggle && onIsNotEnabledChange ? (
            <NotToggle
              filterName={filter.name}
              isNot={isNotEnabled}
              onChange={onIsNotEnabledChange}
            />
          ) : undefined
        }
        bottomContent={
          showToggle ? (
            <ToggleSelect
              filterName={filter.name}
              onChange={(e: number) => setIsCurrent(e === 0)}
              initialIsCurrent={isCurrent}
            />
          ) : undefined
        }
      />
    );
  }

  // The company surfaces returned above with the panel handed to
  // `AllCompaniesPopoutTree`; the inline trees drive it from this form's own
  // pending selection instead.
  const recentsContent = recentsFilterId
    ? renderRecentsPanel({
        selectedIds: selections,
        onToggle: (entity, isSelected) => {
          if (!isSelected) {
            const kept = selections.filter(
              (id) => !matchesShortlistEntity(id, entity)
            );
            forgetPicks(pickSourcesRef.current, kept);
            setSelections(kept);
            return true;
          }
          const next = [...selections, entity.id];
          recordPicks(pickSourcesRef.current, next, 'shortlist');
          return onChange(next);
        },
      })
    : undefined;

  if (!treeData && !hasFetched) {
    return (
      <div
        className={classNames(
          layout.skeletonStack,
          treeSkeletonStyles.themedSkeletons
        )}
      >
        <Skeleton className={layout.skeletonLine} />
        <Skeleton className={layout.skeletonLine} />
        <Skeleton className={layout.skeletonLine} />
        <Skeleton className={layout.skeletonLine} />
        <Skeleton className={layout.skeletonLine} />
      </div>
    );
  }

  return (
    <div className={classNames(layout.formStack, treeStyles.formStack)}>
      {showSelectedValues && selections.length > 0 && (
        <SelectedValuesDisplayComponent
          selections={selections.map((id) => {
            const sel = selectionRecordsForSelectedItems.find(
              (s) => s.id === id
            );
            const treeItem = findTreeDataById(treeData || [], id);
            return { id, name: sel?.name ?? treeItem?.name ?? id, depth: 0 };
          })}
          onRemove={(selectionId) => {
            const kept = selections.filter((id) => id !== selectionId);
            forgetPicks(pickSourcesRef.current, kept);
            setSelections(kept);
          }}
        />
      )}
      <div className={layout.fieldStack}>
        <PopoutTree
          data={treeData || []}
          value={selections}
          sortSelectedIds={selectedIds}
          onChange={handleTreeChange}
          branches={dynamicBranches}
          popoutWidth={
            filter.id === FilterId.ACTIVITY
              ? ACTIVITY_FILTER_POPOUT_WIDTH
              : undefined
          }
          ref={popoutTreeRef}
          sortSuggestedSearch={shouldSortSuggestedSearch}
          disabledLevels={filter.disabledLevels}
          searchInputRef={searchInputRef}
          showNodeCount={showNodeCount}
          renderBranchLabel={
            filter.id === FilterId.GEOGRAPHY
              ? (_branch, drillIndex) =>
                  drillIndex === 2 ? (
                    <GeographyTaxonomyBranchLabel />
                  ) : undefined
              : undefined
          }
          middleContent={
            showNotToggle && onIsNotEnabledChange ? (
              <>
                <NotToggle
                  filterName={filter.name}
                  isNot={isNotEnabled}
                  onChange={onIsNotEnabledChange}
                />
                {recentsContent}
              </>
            ) : (
              recentsContent
            )
          }
        />
        {showToggle && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </div>
      <SelectionListControls
        onClear={handleClear}
        onClose={closeMenu}
        onSubmit={handleSubmit}
        isSubmitDisabled={!selections.length}
        submitText={
          isNewInstance || selectedIds.length === 0 ? 'Add' : 'Update'
        }
      />
    </div>
  );
};
